import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import client from '../../services/apollo/apolloClient/index';
import { RECORD_AI_CONSENT } from '../../services/apollo/mutations/userMutations';
import {
  CONSENT_VERSION,
  hasValidAiConsent,
  recordLocalConsent,
} from '../../utils/aiConsent';
import AiConsentModal from './AiConsentModal';

const AiConsentContext = createContext({
  ensureAiConsent: async () => true,
});

export const AiConsentProvider = ({ children }) => {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const [isOpen, setIsOpen] = useState(false);
  const [part1, setPart1] = useState(false);
  const [part2, setPart2] = useState(false);
  const resolverRef = useRef(null);

  const settle = useCallback((value) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setIsOpen(false);
    setPart1(false);
    setPart2(false);
    if (resolve) resolve(value);
  }, []);

  const ensureAiConsent = useCallback(() => {
    if (hasValidAiConsent()) return Promise.resolve(true);

    return new Promise((resolve) => {
      if (resolverRef.current) resolverRef.current(false);
      resolverRef.current = resolve;
      setPart1(false);
      setPart2(false);
      setIsOpen(true);
    });
  }, []);

  const handleConfirm = useCallback(() => {
    if (!part1 || !part2) return;
    recordLocalConsent();
    if (isAuthenticated) {
      client
        .mutate({
          mutation: RECORD_AI_CONSENT,
          variables: { aiConsent: true, consentVersion: CONSENT_VERSION },
        })
        .catch((error) => {
          console.warn(
            'Failed to persist AI consent to account:',
            error?.message || error
          );
        });
    }
    settle(true);
  }, [part1, part2, isAuthenticated, settle]);

  const handleDecline = useCallback(() => {
    settle(false);
  }, [settle]);

  const value = useMemo(() => ({ ensureAiConsent }), [ensureAiConsent]);

  return (
    <AiConsentContext.Provider value={value}>
      {children}
      <AiConsentModal
        isOpen={isOpen}
        part1Checked={part1}
        part2Checked={part2}
        onPart1Change={setPart1}
        onPart2Change={setPart2}
        onConfirm={handleConfirm}
        onDecline={handleDecline}
      />
    </AiConsentContext.Provider>
  );
};

export const useAiConsent = () => useContext(AiConsentContext);

export default AiConsentProvider;
